import { useState, memo } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import Iconify from 'src/components/Iconify';
import { fToNow } from 'src/utils/formatTime';
import { formataNumeroCelular } from 'src/utils/functions';
import { resolveTags, resolveStatus } from 'src/utils/crmTagColors';
import { CardActionsMenu } from './components/CardActionsMenu';
import NegocioTagPill from './components/NegocioTagPill';
import { NEGOCIOS_DESIGN as D } from 'src/pages/dashboard/Negocios/Negocios.design';
import { HoverCard, HoverCardTrigger, HoverCardContent } from 'src/@/components/ui/hover-card';

const QUALIFICACAO_CONFIG = {
  1: { label: 'Baixa', color: '#3b82f6', bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
  2: { label: 'Média', color: '#f59e0b', bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  3: { label: 'Alta', color: '#f97316', bg: 'bg-orange-50 dark:bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400' },
  4: { label: 'Muito Alta', color: '#ef4444', bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
  5: { label: 'Crítica', color: '#7C3AED', bg: 'bg-violet-50 dark:bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function hashStringToColor(str) {
  if (!str) return '#7C3AED';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#7C3AED', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6'];
  return colors[Math.abs(hash) % colors.length];
}

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const PRIORITY_DOT = {
  low: '#a1a1aa',
  medium: '#d97706',
  high: '#ea580c',
  urgent: '#ef4444',
};
const PRIORITY_LABEL = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

function ymdKey(y, m, d) {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

function isDateOnlyValue(raw) {
  if (!raw) return false;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return true;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  return d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
}

function partsFromDate(raw, dateOnly) {
  if (dateOnly) {
    const iso = typeof raw === 'string' ? raw : new Date(raw).toISOString();
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return { y, m, d, hours: null, minutes: null };
  }
  const dt = new Date(raw);
  return {
    y: dt.getFullYear(),
    m: dt.getMonth() + 1,
    d: dt.getDate(),
    hours: dt.getHours(),
    minutes: dt.getMinutes(),
  };
}

function formatTaskWhen(tarefa) {
  const raw = tarefa?.due_date || tarefa?.created_at;
  if (!raw) return { label: null, tone: 'default' };

  const dateOnly = isDateOnlyValue(raw);
  const parts = partsFromDate(raw, dateOnly);
  if (!parts.y) return { label: null, tone: 'default' };

  const now = new Date();
  const today = ymdKey(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const tomorrow = ymdKey(tomorrowDate.getFullYear(), tomorrowDate.getMonth() + 1, tomorrowDate.getDate());
  const key = ymdKey(parts.y, parts.m, parts.d);

  let dayLabel;
  if (key === today) dayLabel = 'Hoje';
  else if (key === tomorrow) dayLabel = 'Amanhã';
  else if (parts.y === now.getFullYear()) dayLabel = `${parts.d} ${MESES_CURTOS[parts.m - 1]}`;
  else dayLabel = `${pad2(parts.d)}/${pad2(parts.m)}/${String(parts.y).slice(2)}`;

  const timeLabel = parts.hours == null ? null : `${pad2(parts.hours)}:${pad2(parts.minutes)}`;
  const label = timeLabel ? `${dayLabel} ${timeLabel}` : dayLabel;

  let tone = 'default';
  if (tarefa?.due_date) {
    if (key < today) tone = 'overdue';
    else if (key === today) tone = 'today';
  }
  return { label, tone };
}

function getAssigneeName(assignedTo) {
  if (!assignedTo || typeof assignedTo !== 'object') return null;
  return assignedTo.nomeFantasia || assignedTo.nome || null;
}

function TarefaPreviewContent({ tarefa }) {
  const when = formatTaskWhen(tarefa);
  const assignee = getAssigneeName(tarefa.assigned_to);
  const checklist = Array.isArray(tarefa.checklist) ? tarefa.checklist : [];
  const checklistDone = checklist.filter((item) => item.checked).length;
  const description = typeof tarefa.description === 'string' ? tarefa.description.trim() : '';
  const priorityColor = PRIORITY_DOT[tarefa.priority] || PRIORITY_DOT.medium;

  return (
    <div>
      <p className={D.card.taskPreviewTitle}>{tarefa.title || 'Sem título'}</p>
      <div className="flex items-center flex-wrap gap-1 mt-1.5">
        {tarefa.status && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#f4f4f5] text-[#52525b] dark:bg-[#383838] dark:text-[#D1D2D3]">
            {tarefa.status}
          </span>
        )}
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold"
          style={{ backgroundColor: `${priorityColor}18`, color: priorityColor }}
        >
          {PRIORITY_LABEL[tarefa.priority] || PRIORITY_LABEL.medium}
        </span>
        {when.tone === 'overdue' && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#fef3f2] text-[#b42318]">
            Atrasada
          </span>
        )}
      </div>
      {description && <p className={D.card.taskPreviewDesc}>{description}</p>}
      <div className={D.card.taskPreviewMeta}>
        {when.label && (
          <div className={D.card.taskPreviewMetaRow}>
            <Iconify icon="mdi:calendar-clock-outline" width={13} className="text-[#a1a1aa] shrink-0" />
            <span className={when.tone === 'overdue' ? 'text-[#b42318] font-semibold' : when.tone === 'today' ? 'text-[#d97706] font-semibold' : ''}>
              {when.label}
            </span>
          </div>
        )}
        {assignee && (
          <div className={D.card.taskPreviewMetaRow}>
            <Iconify icon="mdi:account-outline" width={13} className="text-[#a1a1aa] shrink-0" />
            <span className="truncate">{assignee}</span>
          </div>
        )}
        {checklist.length > 0 && (
          <div className={D.card.taskPreviewMetaRow}>
            <Iconify icon="mdi:checkbox-marked-circle-outline" width={13} className="text-[#a1a1aa] shrink-0" />
            <span>{checklistDone}/{checklist.length} itens</span>
          </div>
        )}
      </div>
    </div>
  );
}

NegociosCard.propTypes = {
  card: PropTypes.object,
  index: PropTypes.number,
  cardId: PropTypes.string,
  onSelect: PropTypes.func,
  onClickTarefa: PropTypes.func,
  callbackRefreshLeads: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rules: PropTypes.object,
  tagsNegocio: PropTypes.array,
  statusVenda: PropTypes.array,
};

function NegociosCard({ card, index, cardId, onSelect, onClickTarefa, callbackRefreshLeads, userId, rules, tagsNegocio, statusVenda }) {
  const [showContactInfo, setShowContactInfo] = useState(false);

  const qual = QUALIFICACAO_CONFIG[card?.qualificacao];
  const assignedName = card?.nomeUsuario;
  const avatarColor = hashStringToColor(assignedName);
  const valor = card?.valor || 0;

  const status = resolveStatus(card?.status_venda_id, statusVenda);
  const tags = resolveTags(card?.tags, tagsNegocio);
  const tagsVisiveis = tags.slice(0, 3);
  const tagsRestantes = tags.length - tagsVisiveis.length;

  const tarefasPendentes = card?.tarefas_pendentes || [];
  const tarefasVisiveis = tarefasPendentes.slice(0, 3);
  const tarefasRestantes = tarefasPendentes.length - tarefasVisiveis.length;
  const temTarefaAtrasada = tarefasPendentes.some((t) => formatTaskWhen(t).tone === 'overdue');

  return (
    <Draggable draggableId={card?._id} index={index}>
      {(provided, snapshot) => (
        <div
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <div
            onClick={() => onSelect(cardId)}
            className={`${snapshot.isDragging ? D.card.wrapDrag : D.card.wrap}`}
          >
            {/* Row 1: Qualificação fire + Nome */}
            <div className="flex items-start gap-1 mb-1">
              {qual && (
                <Iconify
                  icon="icon-park-solid:fire"
                  width={13}
                  className="shrink-0 mt-[2px]"
                  style={{ color: qual.color }}
                  title={qual.label}
                />
              )}
              <p className={D.card.name}>
                {card?.nome || 'Sem nome'}
              </p>
              {card?.data_modificacao && (
                <span className={D.card.time}>
                  {fToNow(card.data_modificacao)}
                </span>
              )}
            </div>

            {/* Status de venda + tags da negociação */}
            {(status || tagsVisiveis.length > 0) && (
              <div className="flex items-center flex-wrap gap-1 mb-1">
                {status && (
                  <NegocioTagPill nome={status.nome} cor={status.cor_hex} maxWidth="max-w-[120px]" />
                )}
                {tagsVisiveis.map((tag) => (
                  <NegocioTagPill key={tag._id} nome={tag.nome} cor={tag.cor_hex} />
                ))}
                {tagsRestantes > 0 && (
                  <span
                    className="text-[9px] font-medium text-gray-400 dark:text-[#ABABAD]"
                    title={tags.slice(3).map((t) => t.nome).join(', ')}
                  >
                    +{tagsRestantes}
                  </span>
                )}
              </div>
            )}

            {/* Row 2: WhatsApp info */}
            {card?.whatsapp?.numero && (
              <div className="flex items-center gap-1 mb-1 min-w-0">
                <Iconify icon="ri:whatsapp-fill" width={11} className="text-green-500 shrink-0" />
                <span className="text-[10px] font-medium text-gray-600 dark:text-[#D1D2D3] truncate">
                  {formataNumeroCelular(card.whatsapp.numero)}
                </span>
              </div>
            )}
            {(card?.whatsapp?.nome || card?.whatsapp?.lastUpdate) && (
              <div className="flex items-center gap-1.5 mb-1 pl-[15px] min-w-0">
                {card.whatsapp.nome && (
                  <span className="text-[10px] text-gray-400 dark:text-[#ABABAD] truncate">
                    {card.whatsapp.nome}
                  </span>
                )}
                {card.whatsapp.lastUpdate && (
                  <span className="text-[9px] text-gray-400 dark:text-[#ABABAD]/60 shrink-0 flex items-center gap-0.5">
                    <Iconify icon="mdi:clock-outline" width={9} />
                    {fToNow(card.whatsapp.lastUpdate)}
                  </span>
                )}
              </div>
            )}

            {/* Valor */}
            {valor > 0 && (
              <div className="mb-1">
                <span className={D.card.valor}>
                  R$ {valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {tarefasPendentes.length > 0 && (
              <div className={D.card.taskBlock}>
                <div className={D.card.taskHead}>
                  <span className={D.card.taskHeadLabel}>Tarefas</span>
                  <span className={temTarefaAtrasada ? D.card.taskCountOverdue : D.card.taskCount}>
                    {tarefasPendentes.length}
                  </span>
                </div>
                <div className="space-y-1">
                  {tarefasVisiveis.map((tarefa) => {
                    const when = formatTaskWhen(tarefa);
                    const whenClass =
                      when.tone === 'overdue' ? D.card.taskWhenOverdue
                        : when.tone === 'today' ? D.card.taskWhenToday
                          : D.card.taskWhen;
                    return (
                      <HoverCard key={tarefa._id} openDelay={280} closeDelay={80} open={snapshot.isDragging ? false : undefined}>
                        <HoverCardTrigger asChild>
                          <div
                            className={`${D.card.taskRow} rounded-md hover:bg-[#fafafb] dark:hover:bg-[#383838]/40 px-0.5 -mx-0.5`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onClickTarefa?.(tarefa, card);
                            }}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full shrink-0"
                              style={{ backgroundColor: PRIORITY_DOT[tarefa.priority] || PRIORITY_DOT.medium }}
                            />
                            <span className={D.card.taskTitle}>{tarefa.title || 'Sem título'}</span>
                            {when.label && <span className={whenClass}>{when.label}</span>}
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="top"
                          align="start"
                          sideOffset={8}
                          collisionPadding={12}
                          className={D.card.taskPreview}
                        >
                          <TarefaPreviewContent tarefa={tarefa} />
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                </div>
                {tarefasRestantes > 0 && (
                  <p className={D.card.taskMore}>+{tarefasRestantes} {tarefasRestantes === 1 ? 'tarefa' : 'tarefas'}</p>
                )}
              </div>
            )}

            {/* Row 3: Responsável + Actions */}
            <div className="flex items-center justify-between">
              {assignedName ? (
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0"
                    style={{ backgroundColor: avatarColor }}
                    title={assignedName}
                  >
                    {getInitials(assignedName)}
                  </div>
                  <span className={D.card.assignee}>
                    {assignedName}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-[#383838] flex items-center justify-center border border-dashed border-gray-200 dark:border-[#4a4a4a]">
                    <Iconify icon="mdi:account-plus-outline" width={10} className="text-gray-300 dark:text-[#ABABAD]/40" />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-1 shrink-0">
                {card?.contato && (
                  <div
                    className="relative"
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => setShowContactInfo(true)}
                    onMouseLeave={() => setShowContactInfo(false)}
                  >
                    <span className="text-gray-300 dark:text-[#ABABAD]/40 hover:text-[#7C3AED] dark:hover:text-[#7C3AED] cursor-pointer transition-colors" title="Contato">
                      <Iconify icon="mdi:account-circle-outline" width={14} />
                    </span>
                    {showContactInfo && (
                      <div className="absolute bottom-full right-0 mb-1 z-50 w-[200px] p-2.5 rounded-xl shadow-xl bg-slate-50 dark:bg-[#222529] border border-gray-100 dark:border-[#383838] text-left">
                        <div className="flex flex-col gap-1.5">
                          {card.contato.nome && (
                            <div className="flex items-center gap-2">
                              <Iconify width={12} icon="fluent:person-32-filled" className="text-gray-400 shrink-0" />
                              <span className="text-[11px] text-gray-700 dark:text-[#D1D2D3] truncate">{card.contato.nome}</span>
                            </div>
                          )}
                          {card.contato.telefone && (
                            <div className="flex items-center gap-2">
                              <Iconify width={12} icon="ic:phone" className="text-gray-400 shrink-0" />
                              <span className="text-[11px] text-gray-700 dark:text-[#D1D2D3]">{formataNumeroCelular(card.contato.telefone)}</span>
                            </div>
                          )}
                          {card.contato.info?.email && (
                            <div className="flex items-center gap-2">
                              <Iconify width={12} icon="ic:email" className="text-gray-400 shrink-0" />
                              <span className="text-[11px] text-gray-700 dark:text-[#D1D2D3] break-all leading-tight">{card.contato.info.email}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {card?.closed && (
                  <span className="text-gray-400 dark:text-[#ABABAD]/50" title="Finalizado">
                    <Iconify icon="lets-icons:flag-finish" width={12} />
                  </span>
                )}

                {card?.convertido && (
                  <span title={card.convertido === 1 ? 'Venda' : 'Perda'}>
                    <Iconify
                      icon="simple-icons:cashapp"
                      width={12}
                      className={card.convertido === 1 ? 'text-green-500' : 'text-red-500'}
                    />
                  </span>
                )}

                <div onClick={(e) => e.stopPropagation()}>
                  <CardActionsMenu
                    callbackRefreshLeads={callbackRefreshLeads}
                    card={card}
                    userId={userId}
                    rules={rules}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default memo(NegociosCard);
